import React, { useState, useEffect } from "react";
import { Editor, EditorTools } from "@progress/kendo-react-editor";
import { Input } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { ticketApi } from "../redux/api/ticketApi";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { ISearchTicketResult } from "../types/Ticket/Ticket";
import { app, dialog, tasks } from "@microsoft/teams-js";
import { useSearchParams } from "react-router-dom";
import { userApi } from "../redux/api/userApi";
import { IAction } from "../types/Ticket/IAction";
import { Spinny } from "./Spinny";
import ActionConfirmation from "./ActionConfirmation";
import useTenantUrl from "./hooks/useTenantUrl";

const Action: React.FC = () => {
    //get description from the querystring
    const [searchParams] = useSearchParams();
    const description = searchParams.get("description") || "";
    const [note, setNote] = useState<string>(description || "");
    const [billTime, setBillTime] = useState<string>("");
    const [nonBillTime, setNonBillTime] = useState<string>("");
    const [billError, setBillError] = useState<string | null>(null);
    const [nonBillError, setNonBillError] = useState<string | null>(null);
    const [searchTickets, { data, isLoading: searching }] = ticketApi.useSearchTicketsMutation();
    const [ticketOptions, setTicketOptions] = useState<ISearchTicketResult[]>([]);
    const [selectedTicket, setSelectedTicket] = useState<ISearchTicketResult | null>(null);
    const [ticketSearch, setTicketSearch] = useState<string>("");

    // Debounce hook: returns a debounced version of a value after `delay` ms
    function useDebouncedValue<T>(value: T, delay = 500) {
        const [debounced, setDebounced] = useState<T>(value);
        useEffect(() => {
            const handle = setTimeout(() => setDebounced(value), delay);
            return () => clearTimeout(handle);
        }, [value, delay]);
        return debounced;
    }

    const debouncedTicketSearch = useDebouncedValue<string>(ticketSearch, 500);
    const timeRegex = /^\d+:[0-5]\d$/;
    const { data: profile } = userApi.useGetProfileQuery();
    const [addAction, { isLoading: addingAction, error }] = ticketApi.useAddActionMutation();
    const [showConfirmation, setShowConfirmation] = useState(false);


    // Placeholder for Teams SDK
    const submitTeamsTask = async (ticketId: number, note: string, billTime: string, nonBillTime: string) => {
        // Replace with actual Teams SDK submission logic
        // Example: microsoftTeams.tasks.submitTask({ note, billTime, nonBillTime });
        //alert(`Submitted:\nNote: ${note}\nBill Time: ${billTime}\nNon-Bill Time: ${nonBillTime}`);
        await app.initialize();

        //Internal or external action - nevermind, the is handled by the API
        var actionType = profile?.Features.External_Interface ? "external" : "internal";
        try {
            await addAction({ ticketId, action: { Action_Type_Id: actionType == "external" ? 8 : 1, Notes: note, Billable_Time: hhmmToMinutes(billTime), Non_Billable_Time: hhmmToMinutes(nonBillTime) } as IAction }).unwrap();

            //Show the confirmation dialog
            setShowConfirmation(true);
        } catch (error) {
            console.error("Error submitting action:", error);
            setShowConfirmation(false);
        }

    };


    const tenantUrl = useTenantUrl();

    useEffect(() => {
        const query = debouncedTicketSearch;
        if (query && query.length >= 3) {
            (async () => {
                try {
                    const res = await searchTickets(query).unwrap();
                    setTicketOptions(res);
                } catch {
                    setTicketOptions([]);
                }
            })();
        } else {
            setTicketOptions([]);
        }
    }, [debouncedTicketSearch, searchTickets]);

    const confirmClose = () => {
        dialog.url.submit()
    }


    const hhmmToMinutes = (hhmm: string) => {
        if (!hhmm) return 0;
        const [hours, minutes] = hhmm.split(":").map(Number);
        return hours * 60 + minutes;
    }

    const validateTime = (val: string) => {
        if (!val) return null;
        if (/^\d+$/.test(val)) return null; // allow digits-only until blur
        if (timeRegex.test(val)) return null;
        return 'Use H:MM (minutes 00-59)';
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        // normalize single-number inputs
        if (billTime && /^\d+$/.test(billTime)) setBillTime(`${billTime}:00`);
        if (nonBillTime && /^\d+$/.test(nonBillTime)) setNonBillTime(`${nonBillTime}:00`);

        const bErr = validateTime(billTime || "");
        const nbErr = validateTime(nonBillTime || "");
        setBillError(bErr);
        setNonBillError(nbErr);
        if (bErr || nbErr) return;

        await submitTeamsTask(selectedTicket?.ID || 0, note, billTime, nonBillTime);
    };

    const ticketOptionsDisplay = ticketOptions.map(ticket => ({ ...ticket, Title: `#${ticket.ID} - ${ticket.Call_Desc?.substring(0, 50)}` }));
    const isExternal = profile?.Features.External_Interface;

    return (
        <form onSubmit={handleSubmit} className="p-2" style={{ maxWidth: 600, margin: "0 auto" }}>
            {showConfirmation && <ActionConfirmation actionPhrase={profile?.Phrases.Action || "Action"} ticketPhrase={profile?.Phrases.CallPhrase || "Ticket"} ticketId={selectedTicket?.ID || 0} open={showConfirmation} onClose={confirmClose} webBaseUrl={tenantUrl} />}
            {addingAction && <Spinny text="Submitting action..." />}
            <h5>Add {profile?.Phrases.Action || "Action"}</h5>
            {error && <div className="alert alert-danger" role="alert">
                Error while saving action: {JSON.stringify(error)}
            </div>}
            <div style={{ marginBottom: 16 }}>
                <label htmlFor="ticketSearch"><strong>Search Open {profile?.Phrases.CallPhrase}</strong></label>
                <ComboBox
                    required
                    id="ticketSearch"
                    placeholder="Type at least 3 characters..."
                    data={ticketOptionsDisplay}
                    textField="Title"
                    filterable={true}
                    value={selectedTicket}
                    onChange={e => setSelectedTicket(e.value)}
                    onFilterChange={e => {
                        const query = e.filter?.value || "";
                        setTicketSearch(query);
                    }}
                    loading={searching}
                    listNoDataRender={() => searching ? <div className="k-nodata">Searching...</div> : <div className="k-nodata">No results found</div>}
                    style={{ width: "100%" }}
                />
            </div>
            <div style={{ marginBottom: 16 }}>
                <label htmlFor="note"><strong>Note</strong></label>
                <Editor
                    value={note}
                    onChange={e => setNote(e.html)}
                    tools={[
                        [EditorTools.Bold, EditorTools.Italic, EditorTools.Underline],
                        [EditorTools.BulletedList, EditorTools.NumberedList],
                        [EditorTools.Link, EditorTools.Unlink]
                    ]}
                    defaultEditMode="div"
                    style={{ height: 200 }}
                />
            </div>
            {!isExternal && <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
                <div className="col-4">
                    <div className="d-flex">
                        <Input label="Bill Time"
                            id="billTime"
                            type="text"
                            placeholder="hh:mm"
                            value={billTime}
                            onChange={e => {
                                const v = (e.target as unknown as HTMLInputElement).value;
                                setBillTime(v);
                                setBillError(validateTime(v));
                            }}
                            onBlur={() => {
                                if (billTime && /^\d+$/.test(billTime)) {
                                    setBillTime(`${billTime}:00`);
                                    setBillError(null);
                                } else if (billTime && /^\d+:[0-5]$/.test(billTime)) {
                                    // normalize single-minute digit to two digits
                                    const parts = billTime.split(":");
                                    setBillTime(`${parts[0]}:0${parts[1]}`);
                                    setBillError(null);
                                }
                            }}
                            pattern="^\d+:[0-5]\d$"
                            title="Enter hours:minutes (minutes 00-59), e.g. 2:00"

                        />
                        {billError ? <div style={{ color: "#a80000", fontSize: 12 }}>{billError}</div> : null}
                    </div>
                </div>
                <div className="col-3">

                    <Input label="Non-Bill Time"
                        id="nonBillTime"
                        type="text"
                        placeholder="hh:mm"
                        value={nonBillTime}
                        onChange={e => {
                            const v = (e.target as unknown as HTMLInputElement).value;
                            setNonBillTime(v);
                            setNonBillError(validateTime(v));
                        }}
                        onBlur={() => {
                            if (nonBillTime && /^\d+$/.test(nonBillTime)) {
                                setNonBillTime(`${nonBillTime}:00`);
                                setNonBillError(null);
                            } else if (nonBillTime && /^\d+:[0-5]$/.test(nonBillTime)) {
                                const parts = nonBillTime.split(":");
                                setNonBillTime(`${parts[0]}:0${parts[1]}`);
                                setNonBillError(null);
                            }
                        }}
                        pattern="^\d+:[0-5]\d$"
                        title="Enter hours:minutes (minutes 00-59), e.g. 1:45"
                    />
                    {nonBillError ? <div style={{ color: "#a80000", fontSize: 12 }}>{nonBillError}</div> : null}
                </div>
            </div>}
            <div className="d-flex justify-content-end">
                <Button fillMode={"solid"} size={"large"} themeColor="primary" type="submit" disabled={!selectedTicket || !!billError || !!nonBillError}>
                    Submit
                </Button>
            </div>
        </form>
    );
};

export default Action;